import React, {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useState
} from 'react';
import {Spinner} from 'reactstrap';
import {AlertTriangle, Download, FileText} from 'lucide-react';
import {ArtifactRef} from '../api/types';
import {
    ArtifactMap,
    fileNameFromArtifactPath,
    formatFileSize
} from '../artifacts';
import {useAssistantIntegration} from '../AssistantIntegration';

const emptyArtifactMap: ArtifactMap = {
    byPath: new Map(),
    byFileName: new Map()
};

/** The files produced in the thread on screen, for resolving sandbox: links. */
// eslint-disable-next-line react-refresh/only-export-components
export const ArtifactMapContext = createContext<ArtifactMap>(emptyArtifactMap);

// eslint-disable-next-line react-refresh/only-export-components
export const useArtifactMap = (): ArtifactMap => useContext(ArtifactMapContext);

/**
 * Artifact downloads need the bearer token, so plain <img> and <a> tags cannot
 * be used: the file is fetched with the token and shown from a blob URL.
 */
const useArtifactBlobUrl = (downloadPath: string | null | undefined) => {
    const {authenticatedFetch, baseUrl} = useAssistantIntegration();
    const [blobUrl, setBlobUrl] = useState<string | null>(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        if (!downloadPath) {
            return;
        }

        let cancelled = false;
        let createdUrl: string | null = null;

        (async () => {
            try {
                const response = await authenticatedFetch(
                    `${baseUrl}${downloadPath}`
                );
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }

                const blob = await response.blob();
                if (cancelled) {
                    return;
                }

                createdUrl = URL.createObjectURL(blob);
                setBlobUrl(createdUrl);
            } catch {
                if (!cancelled) {
                    setFailed(true);
                }
            }
        })();

        return () => {
            cancelled = true;
            if (createdUrl) {
                URL.revokeObjectURL(createdUrl);
            }
        };
    }, [authenticatedFetch, baseUrl, downloadPath]);

    return {blobUrl, failed};
};

const useArtifactDownload = (downloadPath: string, fileName: string) => {
    const {authenticatedFetch, baseUrl} = useAssistantIntegration();
    const [isDownloading, setIsDownloading] = useState(false);
    const [failed, setFailed] = useState(false);

    const download = useCallback(async () => {
        setIsDownloading(true);
        setFailed(false);
        try {
            const response = await authenticatedFetch(
                `${baseUrl}${downloadPath}`
            );
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const url = URL.createObjectURL(await response.blob());
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = fileName;
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            URL.revokeObjectURL(url);
        } catch {
            setFailed(true);
        } finally {
            setIsDownloading(false);
        }
    }, [authenticatedFetch, baseUrl, downloadPath, fileName]);

    return {download, isDownloading, failed};
};

/** An artifact image, inline in markdown or as a tile. */
export const ArtifactImage: React.FC<{
    downloadPath: string;
    alt?: string;
    maxHeight?: number;
}> = ({downloadPath, alt, maxHeight = 360}) => {
    const {blobUrl, failed} = useArtifactBlobUrl(downloadPath);

    if (failed) {
        return (
            <span className="text-muted small">
                <AlertTriangle size={14} className="me-1" />
                {alt || 'Image'} could not be loaded
            </span>
        );
    }

    if (!blobUrl) {
        return <Spinner size="sm" />;
    }

    return (
        <img
            src={blobUrl}
            alt={alt ?? ''}
            className="img-fluid rounded border"
            style={{maxHeight}}
        />
    );
};

/** A link to an artifact in the assistant's markdown: downloads with the token. */
export const ArtifactDownloadLink: React.FC<{
    downloadPath: string;
    children?: ReactNode;
}> = ({downloadPath, children}) => {
    const fileName = fileNameFromArtifactPath(downloadPath);
    const {download, isDownloading, failed} = useArtifactDownload(
        downloadPath,
        fileName
    );

    return (
        <>
            <button
                type="button"
                className="btn btn-link p-0 align-baseline"
                onClick={download}
                disabled={isDownloading}
                title={`Download ${fileName}`}
            >
                <Download size={14} className="me-1" />
                {children ?? fileName}
            </button>
            {isDownloading && <Spinner size="sm" className="ms-1" />}
            {failed && (
                <span className="text-danger small ms-1">
                    (download failed)
                </span>
            )}
        </>
    );
};

const ArtifactChip: React.FC<{artifact: ArtifactRef}> = ({artifact}) => {
    const {download, isDownloading, failed} = useArtifactDownload(
        artifact.downloadPath ?? '',
        artifact.fileName
    );

    return (
        <button
            type="button"
            className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1"
            onClick={download}
            disabled={isDownloading}
            title={`Download ${artifact.fileName}`}
        >
            {isDownloading ? <Spinner size="sm" /> : <FileText size={14} />}
            <span>{artifact.fileName}</span>
            <span className="text-muted">{formatFileSize(artifact.size)}</span>
            {failed && <span className="text-danger">failed</span>}
        </button>
    );
};

/**
 * The files one runtime call produced: images as tiles, everything else as
 * download chips. A file that could not be saved is always shown, as a warning.
 */
export const ArtifactStrip: React.FC<{artifacts: ArtifactRef[]}> = ({
    artifacts
}) => {
    if (artifacts.length === 0) {
        return null;
    }

    const saved = artifacts.filter(a => a.downloadPath && !a.error);
    const images = saved.filter(a => a.isImage);
    const files = saved.filter(a => !a.isImage);
    const lost = artifacts.filter(a => a.error || !a.downloadPath);

    return (
        <div className="mt-2">
            {images.length > 0 && (
                <div className="d-flex flex-wrap gap-2 mb-2">
                    {images.map(image => (
                        <ArtifactImage
                            key={image.artifactId ?? image.fileName}
                            downloadPath={image.downloadPath!}
                            alt={image.fileName}
                        />
                    ))}
                </div>
            )}
            {(files.length > 0 || images.length > 0) && (
                <div className="d-flex flex-wrap gap-2">
                    {[...files, ...images].map(file => (
                        <ArtifactChip
                            key={file.artifactId ?? file.fileName}
                            artifact={file}
                        />
                    ))}
                </div>
            )}
            {lost.map(file => (
                <div key={file.fileName} className="text-warning small mt-1">
                    <AlertTriangle size={14} className="me-1" />
                    {file.fileName} was generated but could not be saved
                    {file.error ? `: ${file.error}` : '.'}
                </div>
            ))}
        </div>
    );
};
